import React, { useState } from 'react';
import {
  Page,
  Layout,
  Card,
  FormLayout,
  TextField,
  Button,
  Text,
  Banner,
  ProgressBar,
  List,
  Spinner,
} from '@shopify/polaris';
import { useShop } from '../contexts/ShopContext';
import axios from 'axios';
import { getServerUrl } from '../config';

// Get the proxy server URL from the configuration
const PROXY_URL = `${getServerUrl('proxy')}/shopify-proxy`;

function ShopConnect() {
  const [step, setStep] = useState(1);
  const [shopUrl, setShopUrl] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [shopName, setShopName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const { saveShop } = useShop();

  const validateShopUrl = (url) => {
    const shopifyUrlPattern = /^[a-zA-Z0-9][a-zA-Z0-9-]*\.myshopify\.com$/;
    return shopifyUrlPattern.test(url);
  };

  const getShopDomain = (url) => {
    // Extract just the domain part before .myshopify.com
    return url.replace('.myshopify.com', '');
  };

  const handleBack = () => {
    setStep(step - 1);
    setError('');
  };

  const handleNext = () => {
    if (step === 1 && !validateShopUrl(shopUrl)) {
      setError('Please enter a valid Shopify store URL (example.myshopify.com)');
      return;
    }
    setError('');
    setStep(step + 1);
  };

  const validateAccessToken = async (url, token) => {
    try {
      const shopDomain = getShopDomain(url);
      console.log('Using shop domain:', shopDomain);

      // Use our server to validate the shop
      const response = await axios.post(`${getServerUrl('proxy')}/validate-shop`, {
        shop: `${shopDomain}.myshopify.com`,
        accessToken: token
      });

      console.log('Shopify API Response:', response.data);

      const shopData = response.data.shop;
      return {
        id: shopData.id.toString(),
        name: shopData.name,
        email: shopData.email,
        currencyCode: shopData.currency,
        primaryDomain: {
          url: shopData.domain
        },
        billingAddress: {
          countryCodeV2: shopData.country_code
        }
      };
    } catch (error) {
      console.error('Shopify API Error:', error.response?.data || error);
      if (error.response?.status === 401) {
        throw new Error('Invalid access token. Please check your credentials.');
      } else if (error.response?.status === 403) {
        throw new Error('Access denied. Please ensure your app has the required scopes (read_products, write_products).');
      } else if (error.response?.status === 404) {
        throw new Error('Shop not found. Please check the shop URL.');
      } else {
        throw new Error(
          error.response?.data?.error || 
          error.message || 
          'Failed to validate access token. Please ensure your access token has the correct permissions.'
        );
      }
    }
  };

  const handleConnect = async () => {
    if (!accessToken) {
      setError('Please enter your access token');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      // Validate the connection using the access token
      console.log('Validating Shopify connection...');
      const shopInfo = await validateAccessToken(shopUrl, accessToken);
      console.log('Shop info:', shopInfo);

      const shopData = {
        name: shopName,
        shop: shopUrl,
        accessToken,
        createdAt: new Date().toISOString()
      };

      console.log('Connecting shop with data:', {
        ...shopData,
        accessToken: 'hidden'
      });
      
      try {
        await saveShop(shopData);
        // Show success message and reset form
        setSuccess(`Successfully connected to ${shopInfo.name}! You can now connect another shop or proceed to manage your products.`);
        setShopUrl('');
        setAccessToken('');
        setShopName('');
        setStep(1);
      } catch (dbError) {
        console.error('Database error:', dbError);
        setError('Failed to save shop information. Please try again.');
      }
    } catch (err) {
      console.error('Connection error:', err);
      setError(err.message || 'Failed to connect to the shop. Please verify your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <Card sectioned>
      <FormLayout>
        <TextField
          label="Shop URL"
          value={shopUrl}
          onChange={setShopUrl}
          placeholder="your-store.myshopify.com"
          helpText="Enter your Shopify store's URL"
          error={error}
        />
        <Button primary onClick={handleNext}>
          Next
        </Button>
      </FormLayout>
    </Card>
  );

  const renderStep2 = () => (
    <Card sectioned>
      <Text variant="bodyMd" as="p">
        Follow these steps to create a private app and get your access token:
      </Text>
      <List type="bullet">
        <List.Item>
          Go to your Shopify Admin Panel
        </List.Item>
        <List.Item>
          Navigate to Settings → Apps and sales channels → Develop apps → Create an app
        </List.Item>
        <List.Item>
          Configure Admin API access scopes:
          <List type="bullet">
            <List.Item>🛍️ Core Product Management: read_products, write_products</List.Item>
            <List.Item>📦 Inventory Management: read_inventory, write_inventory</List.Item>
            <List.Item>📝 Product Listings: read_product_listings, write_product_listings</List.Item>
            <List.Item>🏬 Location Data: read_locations, write_locations</List.Item>
            <List.Item>🧱 Metaobjects: read_metaobjects, write_metaobjects</List.Item>
            <List.Item>🔧 Metaobject Definitions: read_metaobject_definitions, write_metaobject_definitions</List.Item>
            <List.Item>🖼️ Files and Media: read_files, write_files</List.Item>
            <List.Item>📄 Store Content: read_content, write_content</List.Item>
            <List.Item>📚 Publications: read_publications, write_publications</List.Item>
            <List.Item>💰 Price Rules: read_price_rules, write_price_rules</List.Item>
            <List.Item>🏷️ Discount Functions: read_discounts_allocator_functions, write_discounts_allocator_functions</List.Item>
          </List>
        </List.Item>
      </List>
      <FormLayout>
        <TextField
          label="Access Token"
          value={accessToken}
          onChange={setAccessToken}
          type="password"
          autoComplete="off"
          error={error}
        />
        <TextField
          label="Shop Name (Optional)"
          value={shopName}
          onChange={setShopName}
          placeholder="My Shop"
          helpText="Enter a custom name to identify this store (defaults to your Shopify store name)"
        />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button onClick={handleBack}>Back</Button>
          <Button
            primary
            onClick={handleConnect}
            loading={loading}
            disabled={loading || !accessToken}
          >
            Connect Store
          </Button>
        </div>
      </FormLayout>
    </Card>
  );

  return (
    <Page title="Connect Your Shopify Store">
      <Layout>
        <Layout.Section>
          {success && (
            <Banner status="success" title="Success" onDismiss={() => setSuccess('')}>
              {success}
            </Banner>
          )}
          {error && (
            <Banner status="critical" title="Error" onDismiss={() => setError('')}>
              {error}
            </Banner>
          )}
          <Card sectioned>
            <ProgressBar progress={step === 1 ? 50 : 100} />
          </Card>
          {step === 1 ? renderStep1() : renderStep2()}
        </Layout.Section>
      </Layout>
    </Page>
  );
}

export default ShopConnect;
